import { API_BASE_URL } from './client';

// Widget API functions
export const createWidget = async (keycloak, widgetData) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/widgets/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(widgetData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create widget');
  }
  return await response.json();
};

export const getWidgets = async (keycloak, assistantId = null, options = {}) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const { showAll = false } = options;
  const params = new URLSearchParams();
  if (assistantId) {
    params.append('assistant_id', assistantId);
  }
  if (showAll) {
    params.append('show_all', 'true');
  }
  const qs = params.toString();
  const url = `${API_BASE_URL}/widgets/${qs ? `?${qs}` : ''}`;
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    const d = error.detail;
    const msg = Array.isArray(d)
      ? d.map((x) => (typeof x === 'string' ? x : x.msg || JSON.stringify(x))).join('; ')
      : (d || 'Failed to fetch widgets');
    throw new Error(msg);
  }
  return await response.json();
};

export const getWidget = async (keycloak, widgetId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/widgets/${widgetId}/`, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch widget');
  }
  return await response.json();
};

export const updateWidget = async (keycloak, widgetId, widgetData) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/widgets/${widgetId}/`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(widgetData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update widget');
  }
  return await response.json();
};

export const deleteWidget = async (keycloak, widgetId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/widgets/${widgetId}/`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    const d = error.detail;
    const msg = Array.isArray(d)
      ? d.map((x) => (typeof x === 'string' ? x : x.msg || JSON.stringify(x))).join('; ')
      : (d || 'Failed to delete widget');
    throw new Error(msg);
  }
  return await response.json();
};

export const regenerateWidgetToken = async (keycloak, widgetId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/widgets/${widgetId}/regenerate-token/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to regenerate widget token');
  }
  return await response.json();
};

export const getWidgetSessions = async (keycloak, widgetId, activeOnly = false) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  let url = `${API_BASE_URL}/widgets/${widgetId}/sessions/`;
  if (activeOnly) {
    url += '?active_only=true';
  }
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch widget sessions');
  }
  return await response.json();
};

export const getWidgetSessionMessages = async (keycloak, widgetId, sessionId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/widgets/${widgetId}/sessions/${sessionId}/messages/`, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch session messages');
  }
  return await response.json();
};

export const searchWidgetSessionMessages = async (keycloak, widgetId, q, limit = 20) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  params.set('limit', String(limit));
  const response = await fetch(
    `${API_BASE_URL}/widgets/${widgetId}/sessions/search/?${params.toString()}`,
    {
      headers: {
        'Authorization': `Bearer ${keycloak.token}`,
      },
    }
  );
  if (!response.ok) {
    const error = await response.json();
    const d = error.detail;
    const msg = typeof d === 'string' ? d : (d && JSON.stringify(d)) || 'Search failed';
    throw new Error(msg);
  }
  return await response.json();
};

export const patchWidgetSessionSummary = async (keycloak, widgetId, sessionId, summary) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(
    `${API_BASE_URL}/widgets/${widgetId}/sessions/${encodeURIComponent(sessionId)}/`,
    {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${keycloak.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ summary: summary ?? '' }),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    const d = error.detail;
    const msg = typeof d === 'string' ? d : (d && JSON.stringify(d)) || 'Failed to update session';
    throw new Error(msg);
  }
  return await response.json();
};

export const regenerateWidgetSessionSummary = async (keycloak, widgetId, sessionId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(
    `${API_BASE_URL}/widgets/${widgetId}/sessions/${encodeURIComponent(sessionId)}/regenerate-summary/`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${keycloak.token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(
      (typeof error.detail === 'string' && error.detail) || 'Failed to regenerate summary'
    );
  }
  return await response.json();
};

export const getWidgetStatistics = async (keycloak, widgetId, startDate = null, endDate = null) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  let url = `${API_BASE_URL}/widgets/${widgetId}/statistics/`;
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (params.toString()) url += `?${params.toString()}`;
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch widget statistics');
  }
  return await response.json();
};

export const getWidgetEmbedCode = async (keycloak, widgetId) => {
  if (!keycloak || !keycloak.token) {
    throw new Error('Keycloak not initialized or no token available');
  }
  const response = await fetch(`${API_BASE_URL}/widgets/${widgetId}/embed-code/`, {
    headers: {
      'Authorization': `Bearer ${keycloak.token}`,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch embed code');
  }
  return await response.json();
};
